import { useAuth } from "@/contexts/AuthContext";
import { useSelection } from "@/contexts/SelectionContext";
import { useMoveLessonToTrash } from "@/lib/hooks/lessons/useMoveLessonsToTrash";
import { useMoveProjectsToTrash } from "@/lib/hooks/projects/useMoveProjectsToTrash";
import { useMoveSlidesToTrash } from "@/lib/hooks/slides/useMoveSlidesToTrash";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { type ErrorCode, ErrorCodes } from "@ai-teach/types";
import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";

interface TrashModalProps {
  isOpen: boolean;
  sourceId: string | null;
}

const SUPERUSER_OVERRIDABLE_ERRORS: ErrorCode[] = [
  ErrorCodes.CANNOT_TRASH_WITH_NON_DELETED_CHILD,
  ErrorCodes.CANNOT_TRASH_ORIGINAL_WITH_LINKED_COPIES,
];

const OVERRIDE_MESSAGES: Record<string, string> = {
  [ErrorCodes.CANNOT_TRASH_WITH_NON_DELETED_CHILD]:
    "This item has non-deleted children. Are you sure ?",
  [ErrorCodes.CANNOT_TRASH_ORIGINAL_WITH_LINKED_COPIES]:
    "This item has linked copies. Are you sure ?",
};

export function TrashModal({ isOpen, sourceId }: TrashModalProps) {
  const {
    selectedProjectIds,
    selectedLessonIds,
    selectedSlideIds,
    clearAllSelections,
    toggleTrashModal,
    toggleIsSelectionMode,
  } = useSelection();

  const { user } = useAuth();

  const [errorMessage, setErrorMessage] = useState<{
    code: ErrorCode | null;
    message: string;
  } | null>(null);

  const {
    moveProjectsToTrash,
    loading: projectTrashLoading,
    error: projectTrashError,
  } = useMoveProjectsToTrash();
  const {
    moveLessonsToTrash,
    loading: lessonTrashLoading,
    error: lessonTrashError,
  } = useMoveLessonToTrash();
  const {
    moveSlidesToTrash,
    loading: slideTrashLoading,
    error: slideTrashError,
  } = useMoveSlidesToTrash(sourceId);

  const handleClose = () => {
    const totalItems = selectedLessonIds.length + selectedProjectIds.length;
    if (totalItems === 1) {
      clearAllSelections();
    } else {
      toggleIsSelectionMode(true);
    }
    setErrorMessage(null);
    toggleTrashModal(false);
  };

  const handleTrash = async (skipCheck: boolean) => {
    try {
      setErrorMessage(null);

      if (selectedLessonIds.length > 0) {
        await moveLessonsToTrash(selectedLessonIds, skipCheck);
      }

      if (selectedProjectIds.length > 0) {
        await moveProjectsToTrash(selectedProjectIds, skipCheck);
      }

      if (selectedSlideIds.length > 0) {
        await moveSlidesToTrash(selectedSlideIds, skipCheck);
      }

      clearAllSelections();
      toggleTrashModal(false);
    } catch (error) {
      for (const code of SUPERUSER_OVERRIDABLE_ERRORS) {
        if (hasGraphQLErrorCode(error, code)) {
          setErrorMessage({ code, message: (error as Error).message });
          return;
        }
      }

      setErrorMessage({
        code: null,
        message: "Failed to move items to trash. Please try again.",
      });
    }
  };

  const isLoading = projectTrashLoading || lessonTrashLoading || slideTrashLoading;
  const isError = !!(projectTrashError || lessonTrashError || slideTrashError);
  const isSuperuser = user?.role && ["super", "superuser"].includes(user?.role);

  const canOverride =
    isSuperuser && errorMessage?.code && SUPERUSER_OVERRIDABLE_ERRORS.includes(errorMessage.code);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={"Warning!"} size="sm" style="danger">
      <div className="flex flex-col">{renderContent()}</div>
    </Modal>
  );

  function renderContent() {
    if (!errorMessage?.code) {
      return (
        <>
          <p className="text-secondary-200 mb-6 text-center">
            This item will be moved to the trash. If you change your mind, you can recover it from
            there.
          </p>
          <div className="flex gap-3 justify-between">
            <Button onClick={handleClose} variant="secondary">
              Cancel
            </Button>
            <Button
              onClick={() => handleTrash(false)}
              variant="danger"
              disabled={isLoading || isError}
            >
              Delete
            </Button>
          </div>
        </>
      );
    }

    if (canOverride) {
      return (
        <>
          <p className="text-secondary-200 mb-6 text-center">
            {OVERRIDE_MESSAGES[errorMessage.code] ?? errorMessage.message}
          </p>
          <div className="flex justify-between">
            <Button onClick={handleClose} variant="secondary">
              Cancel
            </Button>
            <Button onClick={() => handleTrash(true)} variant="danger">
              Continue
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <p className="text-secondary-200 mb-6 text-center">{errorMessage.message}</p>
        <div className="flex justify-center">
          <Button onClick={handleClose} variant="secondary">
            Close
          </Button>
        </div>
      </>
    );
  }
}
